import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useDecisoes } from '@hooks/useDecisoes.js'
import { PageShell } from '@components/layout/PageShell.js'
import { ModePageHeader } from '@components/layout/ModePageHeader.js'
import { Button } from '@components/primitives/Button.js'
import { EmptyPanel } from '@components/primitives/EmptyPanel.js'
import { CardListSkeleton, PageHeaderSkeleton } from '@components/primitives/Skeleton.js'
import { DecisaoFormModal } from '@components/decisoes/DecisaoFormModal.js'
import type { Decisao } from '@services/db.js'
import type { DecisaoCatalogo } from '@content/decisoes-catalogo.js'
import { FASES_CONSTRUTIVAS } from '@utils/constants.js'
import { formatDate } from '@utils/format.js'

const FASE_LABEL = new Map(FASES_CONSTRUTIVAS.map((f) => [f.id, f.label]))

export function DecisoesPage() {
  const { registradas, pendentes, travando, isLoading, registrar, editar, remover } = useDecisoes()
  const [searchParams, setSearchParams] = useSearchParams()
  const chaveParam = searchParams.get('chave')
  const [modal, setModal] = useState<{
    open: boolean
    initial: Decisao | null
    catalogo: DecisaoCatalogo | null
  }>({ open: false, initial: null, catalogo: null })

  useEffect(() => {
    if (isLoading || !chaveParam) return
    const item = [...travando, ...pendentes].find((c) => c.chave === chaveParam)
    if (item) setModal({ open: true, initial: null, catalogo: item })
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.delete('chave')
        return next
      },
      { replace: true },
    )
  }, [chaveParam, isLoading, pendentes, setSearchParams, travando])

  if (isLoading) {
    return (
      <PageShell>
        <PageHeaderSkeleton label='Carregando decisões…' />
        <section className='cf3-block'>
          <CardListSkeleton count={3} label='Carregando memorial…' />
        </section>
      </PageShell>
    )
  }

  return (
    <PageShell>
      <ModePageHeader
        title='Memorial de decisões'
        subtitle='O que já travou e não dá para mudar sem quebrar. Registre antes de fechar parede, laje ou contrapiso.'
      >
        <Button
          variant='primary'
          size='sm'
          onClick={() => setModal({ open: true, initial: null, catalogo: null })}
        >
          Nova decisão
        </Button>
      </ModePageHeader>

      {travando.length > 0 && (
        <section className='cf3-block' aria-labelledby='trava-heading'>
          <h2 id='trava-heading' className='cf3-subsection-title'>
            Trava nesta fase
          </h2>
          <ul className='cf3-decisao-lista'>
            {travando.map((c) => (
              <li key={c.chave}>
                <button
                  type='button'
                  className='cf3-decisao cf3-decisao--pendente'
                  onClick={() => setModal({ open: true, initial: null, catalogo: c })}
                >
                  <span className='cf3-decisao__titulo'>{c.titulo}</span>
                  <span className='cf3-decisao__motivo'>{c.motivo}</span>
                  <span className='cf3-decisao__acao'>Registrar</span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {pendentes.length > travando.length && (
        <section className='cf3-block' aria-labelledby='pend-heading'>
          <h2 id='pend-heading' className='cf3-subsection-title'>
            Ainda sem registro
          </h2>
          <ul className='cf3-decisao-lista'>
            {pendentes
              .filter((c) => !travando.some((t) => t.chave === c.chave))
              .map((c) => (
                <li key={c.chave}>
                  <button
                    type='button'
                    className='cf3-decisao cf3-decisao--pendente'
                    onClick={() => setModal({ open: true, initial: null, catalogo: c })}
                  >
                    <span className='cf3-decisao__titulo'>{c.titulo}</span>
                    <span className='cf3-decisao__motivo'>{c.motivo}</span>
                    <span className='cf3-decisao__acao'>Registrar</span>
                  </button>
                </li>
              ))}
          </ul>
        </section>
      )}

      {registradas.length === 0 && pendentes.length === 0 && (
        <EmptyPanel
          title='Nenhuma decisão no memorial'
          description='Anote sistema construtivo, vãos, prumadas e impermeabilização. É a prova do que foi combinado quando alguém disser que não estava no projeto.'
          actionLabel='Registrar primeira decisão'
          onAction={() =>
            setModal({
              open: true,
              initial: null,
              catalogo: pendentes[0] ?? null,
            })
          }
        />
      )}

      {registradas.length > 0 && (
        <section className='cf3-block' aria-labelledby='reg-heading'>
          <h2 id='reg-heading' className='cf3-subsection-title'>
            Registradas ({registradas.length})
          </h2>
          <ul className='cf3-decisao-lista'>
            {registradas.map((d) => (
              <li key={d.id}>
                <article className='cf3-decisao'>
                  <p className='cf3-decisao__titulo'>{d.titulo}</p>
                  <p className='cf3-decisao__escolha'>{d.escolha}</p>
                  <p className='cf3-decisao__meta'>
                    {FASE_LABEL.get(d.faseId) ?? d.faseId}
                    {d.travaEm ? ` · trava em ${FASE_LABEL.get(d.travaEm) ?? d.travaEm}` : ''}
                    {` · ${formatDate(d.criadoEm.slice(0, 10))}`}
                  </p>
                  <div className='cf3-gestao-actions'>
                    <Button
                      variant='ghost'
                      size='sm'
                      onClick={() => setModal({ open: true, initial: d, catalogo: null })}
                    >
                      Editar
                    </Button>
                    {d.id != null && (
                      <Button variant='ghost' size='sm' onClick={() => void remover(d.id!)}>
                        Excluir
                      </Button>
                    )}
                  </div>
                </article>
              </li>
            ))}
          </ul>
        </section>
      )}

      <DecisaoFormModal
        open={modal.open}
        initial={modal.initial}
        catalogo={modal.catalogo}
        onClose={() => setModal({ open: false, initial: null, catalogo: null })}
        onSalvar={registrar}
        onEditar={editar}
      />
    </PageShell>
  )
}
